import React from 'react';
import { Alert, Intent } from '@blueprintjs/core';
import { observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import qs from 'qs';
import moment from 'moment-timezone';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import Toast from 'helpers/toaster';
import { currentTime } from 'helpers/time';
import * as DataList from 'components/DataList';

// A client's locations (client page, Locations tab), in the same list style
// as the other client tabs. Locations have no list screen of their own.
const COLUMNS = [
  { area: 'address', label: 'Address', width: 'minmax(240px, 2fr)', icon: 'map-marker' },
  { area: 'notes', label: 'Notes', width: 'minmax(180px, 1.4fr)', bone: 'single', icon: 'align-left' },
  { area: 'added', label: 'Added', width: '128px', bone: 'single', icon: 'calendar' },
  { area: 'actions', label: '', width: '64px', bone: 'none' },
];

const CARDS = {
  columns: 'minmax(0, 1fr) auto',
  areas: [
    'address actions',
    'notes notes',
    'added added',
  ],
  footer: ['added'],
};

const MIN_TABLE_WIDTH = 640;

@inject('viewStore', 'geolocationStore') @observer
class LocationsTable extends React.Component {

  @observable isDeleteDialogVisible = false;
  @observable location;

  componentDidMount() {
    this.props.viewStore.clientLocationSearch.where.client_id = this.props.client.attributes.id;
    this.props.viewStore.locationWorkReportSearch.where.client_id = this.props.client.attributes.id;
    this.props.geolocationStore.loadClientLocations();
  }

  onPaginationChange(newPage) {
    const { viewStore } = this.props;
    viewStore.clientLocationSearch = {
      ...viewStore.clientLocationSearch,
      page: newPage,
    };
    this.props.geolocationStore.loadClientLocations();
  }

  workOrderSearchQuery = (location) => qs.stringify(
    {
      where: {
        grand_total: {
          gte: null,
          lte: null,
        },
        created_at: {
          gte: moment().subtract(1, 'month').unix(),
          lte: moment().unix(),
        },
        kind: 'WorkReport',
        location_id: location.id,
        client_id: location.attributes.client_id,
      },
    },
    { encode: false }
  );

  handleDeleteClick(event, location) {
    this.isDeleteDialogVisible = true;
    this.location = location;
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
    this.location = null;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    this.props.geolocationStore.deleteLocation(this.location)
      .then(this.onSuccessfulDeleteLocation.bind(this))
      .catch(this.onFailedLocationDelete.bind(this));
  }

  onSuccessfulDeleteLocation() {
    Toast.show({ icon: 'thumbs-up', message: 'Location deleted', intent: Intent.SUCCESS });
    this.location = null;
    this.isDeleteDialogVisible = false;
  }

  onFailedLocationDelete() {
    this.isDeleteDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Couldn\'t delete the location', intent: Intent.DANGER });
  }

  renderDeleteAlert() {
    const name = this.location && (this.location.attributes.name || this.location.attributes.full_address);
    return (
      <Alert
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        icon="trash"
        intent={Intent.DANGER}
        isOpen={this.isDeleteDialogVisible}
        onCancel={this.handleDeleteCancel.bind(this)}
        onConfirm={this.handleDeleteConfirm.bind(this)}
      >
        <p>
          Delete the location <b>{name}</b>?
        </p>
      </Alert>
    );
  }

  render() {
    const {
      geolocationStore: {
        clientLocations: { objects, isLoading, isError, meta },
      },
    } = this.props;

    if (isLoading && objects.length === 0) {
      return (
        <DataList.Loading columns={COLUMNS} cards={CARDS} minTableWidth={MIN_TABLE_WIDTH} noun="locations" rows={5} />
      );
    }
    if (isError) {
      return (
        <DataList.LoadError
          noun="locations"
          onRetry={() => this.onPaginationChange((meta && meta.page) || 1)}
        />
      );
    }
    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="locations"
          message="Addresses you add for this client will show up here."
        />
      );
    }

    const pagination = meta && meta.total_pages > 1 && (
      <Footer>
        <div className="bp3-navbar-group bp3-align-left">
          <Pagination value={meta.page} total={meta.total_pages} onChange={this.onPaginationChange.bind(this)} />
        </div>
      </Footer>
    );

    return (
      <React.Fragment>
        <DataList.List
          preferTable
          columns={COLUMNS}
          cards={CARDS}
          minTableWidth={MIN_TABLE_WIDTH}
          total={(meta && meta.total_records) || objects.length}
          noun="locations"
        >
          {objects.map((location) => {
            const attributes = location.attributes;
            const address = attributes.full_address;
            const name = attributes.name && attributes.name !== address ? attributes.name : null;
            return (
              <DataList.Row key={location.id}>
                <DataList.Cell area="address">
                  {address ? (
                    <DataList.Lines primary={address} secondary={name} />
                  ) : (
                    <DataList.Lines primary={name || <DataList.Muted>No address</DataList.Muted>} />
                  )}
                </DataList.Cell>
                <DataList.Cell area="notes">
                  {attributes.notes ? (
                    <DataList.Lines primary={attributes.notes} strong={false} title={attributes.notes} />
                  ) : (
                    <DataList.Muted>No notes</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="added">
                  {attributes.created_at ? (
                    <DataList.Lines
                      primary={
                        <React.Fragment>
                          <DataList.CardOnly>Added </DataList.CardOnly>
                          {currentTime(attributes.created_at).format('MMM D, YYYY')}
                        </React.Fragment>
                      }
                      strong={false}
                    />
                  ) : (
                    <DataList.Muted>Unknown</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      {
                        icon: 'document-open',
                        label: 'View work orders',
                        href: `/dashboard/work-reports?${this.workOrderSearchQuery(location)}`,
                      },
                      {
                        icon: 'trash',
                        label: 'Delete location',
                        intent: 'danger',
                        onClick: () => this.handleDeleteClick({ label: 'Delete' }, location),
                      },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        {pagination}
        {this.renderDeleteAlert()}
      </React.Fragment>
    );
  }
}

export default LocationsTable;
